// Data glue for the engineer PWA message outbox: ties the IndexedDB adapter (db.ts), the pure
// drain logic (messageSync.ts) and the API client together. The React layer calls these; it never
// touches IndexedDB or fetch directly. Mirrors jobsStore.ts.
import { api, ApiError } from "../api";
import { messageOutboxStore } from "./db";
import { flushMessages, classifyMessageSend, clearMessageEscalation } from "./messageSync";
import type { QueuedMessage, MessageFlushSummary } from "./messageSync";

// Queue a reply. Persists to the outbox immediately (survives a reload / no signal), so an
// engineer's reply is never lost before it syncs. No recipient is stored -- the server resolves the
// office recipient for an engineer sender. A clientKey (UUID) is stamped so a lost-response retry
// dedupes server-side instead of writing a duplicate message (Session AFF).
export async function enqueueMessage(input: { body: string; jobId?: string }): Promise<QueuedMessage> {
  const item: QueuedMessage = {
    id: crypto.randomUUID(),
    body: input.body,
    jobId: input.jobId,
    queuedAt: new Date().toISOString(),
    attempts: 0,
    clientKey: crypto.randomUUID(),
  };
  await messageOutboxStore.putMessage(item);
  return item;
}

// The engineer's queued (not-yet-sent) replies, for the UI to show as pending / escalated.
export async function getQueuedMessages(): Promise<QueuedMessage[]> {
  return (await messageOutboxStore.getMessageOutbox()).slice().sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

// Re-arm an escalated reply so the next drain retries it (the engineer's "Retry" control). Clears
// the escalation + strike state; the retry earns a fresh set of spaced strikes if it fails again.
export async function retryQueuedMessage(id: string): Promise<void> {
  const items = await messageOutboxStore.getMessageOutbox();
  const item = items.find((i) => i.id === id);
  if (!item) return;
  await messageOutboxStore.putMessage(clearMessageEscalation(item));
}

// Discard a queued reply the engineer no longer wants to send (the "Remove" control on an escalated
// message). The only user-facing delete for the message outbox.
export async function removeQueuedMessage(id: string): Promise<void> {
  await messageOutboxStore.deleteMessage(id);
}

// Send one queued reply. A 2xx clears it; a structural 4xx is classified so a persistent run
// escalates; any other status / a network error is kept and retried. The clientKey rides the
// request so a lost-response retry dedupes server-side; the server's own message (err.code) is
// carried through so an escalated item has a human reason.
const send = async (item: QueuedMessage) => {
  try {
    await api.messages.send({ body: item.body, jobId: item.jobId, clientKey: item.clientKey });
    return { ok: true as const };
  } catch (err) {
    if (err instanceof ApiError) return classifyMessageSend(err.status, err.code);
    throw err; // network/offline -> retryable
  }
};

// Drain the message outbox. Guarded so overlapping triggers (reconnect + foreground + the service
// worker's background-sync message + a manual send) never run concurrently.
let flushing = false;
export async function syncMessages(): Promise<MessageFlushSummary | null> {
  if (flushing) return null;
  flushing = true;
  try {
    return await flushMessages(messageOutboxStore, send);
  } finally {
    flushing = false;
  }
}
